import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { scanVbaCode, RiskFlag } from "@/lib/riskScanner";
import { toast } from "sonner";
import { Upload, AlertTriangle, Plus, X, ChevronDown, FileUp, Eye, Save, ArrowLeft, MoreVertical } from "lucide-react";
import { cn } from "@/lib/utils";
import { SubmissionPreview } from "@/components/SubmissionPreview";
import { ImageCropModal } from "@/components/ImageCropModal";
import { AddVbaCodeModal } from "@/components/AddVbaCodeModal";
import { CodeEditor } from "@/components/CodeEditor";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "@/components/RichTextEditor.css";

// Validation schema for macro submission
const macroSchema = z.object({
  name: z.string().trim().min(3, "Name must be at least 3 characters").max(100, "Name must be less than 100 characters"),
  shortDesc: z.string().trim().min(1, "Description is required").max(1000, "Description must be less than 1,000 characters"),
  installationGuide: z.string().max(10000, "Installation guide must be less than 10,000 characters"),
  semver: z.string().regex(/^\d+\.\d+\.\d+$/, "Version must be in format X.Y.Z (e.g., 1.0.0)"),
  modules: z.array(z.object({
    name: z.string().trim().min(1, "Module name is required").max(50, "Module name must be less than 50 characters"),
    code: z.string().min(1, "Module code is required").max(50000, "Module code must be less than 50,000 characters")
  })).min(1, "At least one module is required"),
  category: z.number({
    required_error: "Category is required"
  }).min(1, "Please select a category")
});
interface VbaModule {
  id: string;
  name: string;
  code: string;
  riskScan: {
    score: string;
    flags: RiskFlag[];
  } | null;
}
interface ExcelFile {
  id: string;
  name: string;
  size: number;
  file: File;
}
const Submit = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    user
  } = useAuth();
  const [editingMacroId, setEditingMacroId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [installationGuide, setInstallationGuide] = useState("");
  const [modules, setModules] = useState<VbaModule[]>([{
    id: "1",
    name: "Module 1",
    code: "",
    riskScan: null
  }]);
  const [license, setLicense] = useState("MIT");
  const [osSupport, setOsSupport] = useState<string[]>(["windows", "mac"]);
  const [excelVersions, setExcelVersions] = useState<string[]>(["O365"]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [semver, setSemver] = useState("1.0.0");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string>("");
  const [excelFiles, setExcelFiles] = useState<ExcelFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [privateCollectionId, setPrivateCollectionId] = useState<string | null>(null);
  const [savingPrivate, setSavingPrivate] = useState(false);
  const [privateJustSaved, setPrivateJustSaved] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState<string>("");
  const [originalImageSrc, setOriginalImageSrc] = useState<string>("");
  const [moduleToDelete, setModuleToDelete] = useState<string | null>(null);
  const [openModules, setOpenModules] = useState<Set<string>>(new Set(["1"]));
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [codeEditorRef, setCodeEditorRef] = useState<HTMLTextAreaElement | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [originalCodeText, setOriginalCodeText] = useState<string>("");
  const [originalRiskScore, setOriginalRiskScore] = useState<string | null>(null);
  const [originalRiskFlags, setOriginalRiskFlags] = useState<RiskFlag[]>([]);

  // Track changes for unsaved changes warning
  useEffect(() => {
    if (name || shortDesc || installationGuide || modules.some(m => m.code) || screenshot || selectedTags.length > 0) {
      setHasUnsavedChanges(true);
    }
  }, [name, shortDesc, installationGuide, modules, screenshot, selectedTags]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }

    // Check for edit parameter
    const editId = searchParams.get("edit");
    if (editId) {
      loadMacroForEdit(editId);
    }
    fetchTags();
  }, [navigate, searchParams, user]);
  const fetchTags = async () => {
    const {
      data,
      error
    } = (await (supabase as any).from("tags").select("*").order("name")) as any;
    if (!error && data) {
      setTags(data);
    }
  };
  const loadMacroForEdit = async (macroId: string) => {
    try {
      const {
        data: macro,
        error
      } = await supabase.from("macros").select(`
          *,
          macro_tags (
            tag_id
          )
        `).eq("id", macroId).single();
      if (error) throw error;
      if (macro) {
        setEditingMacroId(macroId);
        setName(macro.name || "");
        setShortDesc(macro.short_desc || "");
        setInstallationGuide(macro.installation_guide || "");

        // Store original risk data
        setOriginalRiskScore(macro.risk_score || null);
        setOriginalRiskFlags(macro.risk_flags as unknown as RiskFlag[] || []);

        // Load version and code directly from macro
        setSemver(macro.semver || "1.0.0");

        if (macro.code_text) {
          // Store original code for comparison
          setOriginalCodeText(macro.code_text);

          // Preserve existing risk scan data
          setModules([{
            id: "1",
            name: "Module 1",
            code: macro.code_text,
            riskScan: {
              score: macro.risk_score || 'unknown',
              flags: macro.risk_flags as unknown as RiskFlag[] || []
            }
          }]);
        }

        // Load tags
        if (macro.macro_tags && macro.macro_tags.length > 0) {
          setSelectedTags(macro.macro_tags.map((mt: any) => mt.tag_id));
        }

        // Load screenshot
        if (macro.screenshot_url) {
          setScreenshotPreview(macro.screenshot_url);
        }
        setHasUnsavedChanges(false); // Reset on load
        toast.success("Macro loaded for editing");
      }
    } catch (error: any) {
      console.error("Error loading macro:", error);
      toast.error("Failed to load macro for editing");
    }
  };
  const loadPrivateCollection = async (userId: string) => {
    try {
      const {
        data,
        error
      } = await supabase.from("macro_drafts").select("*").eq("user_id", userId).order("updated_at", {
        ascending: false
      }).limit(1).maybeSingle();
      if (error) throw error;
      if (data) {
        setPrivateCollectionId(data.id);
        setName(data.title || "");
        setShortDesc(data.description || "");
        setInstallationGuide(data.installation_guide || "");
        setSemver(data.version || "1.0.0");
        if (data.modules && Array.isArray(data.modules) && data.modules.length > 0) {
          setModules(data.modules as unknown as VbaModule[]);
        }
        if (data.excel_files && Array.isArray(data.excel_files)) {
          // Note: Can't restore actual File objects, just show metadata
          const fileMetadata = data.excel_files.map((f: any, idx: number) => ({
            id: `restored-${idx}`,
            name: f.name,
            size: f.size,
            file: null as any // Can't restore File objects
          }));
          setExcelFiles(fileMetadata);
        }
        toast.success("Private collection loaded");
      }
    } catch (error: any) {
      console.error("Error loading private collection:", error);
    }
  };
  const saveForMyself = async () => {
    if (!user) {
      toast.error("You must be logged in to save");
      return;
    }

    // Only require macro name
    if (!name.trim()) {
      toast.error("Please add a macro name before saving");
      return;
    }
    setSavingPrivate(true);
    try {
      const privateData = {
        user_id: user.id,
        title: name,
        description: shortDesc,
        installation_guide: installationGuide,
        modules: modules as any,
        excel_files: excelFiles.map(f => ({
          name: f.name,
          size: f.size
        })) as any,
        version: semver
      };
      if (privateCollectionId) {
        // Update existing private collection
        const {
          error
        } = await supabase.from("macro_drafts").update(privateData).eq("id", privateCollectionId);
        if (error) throw error;
        toast.success("Saved to your collection");
      } else {
        // Create new private collection entry
        const {
          data,
          error
        } = await supabase.from("macro_drafts").insert([privateData]).select().single();
        if (error) throw error;
        setPrivateCollectionId(data.id);
        toast.success("Saved to your collection");
      }

      // Visual feedback - turn button green briefly
      setPrivateJustSaved(true);
      setTimeout(() => setPrivateJustSaved(false), 2000);
      setHasUnsavedChanges(false); // Reset after saving
    } catch (error: any) {
      toast.error(error.message || "Failed to save");
    } finally {
      setSavingPrivate(false);
    }
  };
  const processImageFile = (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Screenshot must be less than 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const imgSrc = reader.result as string;
      setOriginalImageSrc(imgSrc);
      setTempImageSrc(imgSrc);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };
  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processImageFile(file);
    }
  };
  const handleImageDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingImage(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      processImageFile(file);
    } else {
      toast.error("Please drop an image file");
    }
  };
  const handleImageDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingImage(true);
  };
  const handleImageDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingImage(false);
  };
  const handleReCrop = () => {
    const imageToRecrop = originalImageSrc || screenshotPreview;
    if (imageToRecrop) {
      setTempImageSrc(imageToRecrop);
      setCropModalOpen(true);
    }
  };
  const handleCropComplete = (croppedFile: File) => {
    setScreenshot(croppedFile);
    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshotPreview(reader.result as string);
    };
    reader.readAsDataURL(croppedFile);
    toast.success("Image cropped successfully");
  };
  const handleAddModule = () => {
    const newModule: VbaModule = {
      id: Date.now().toString(),
      name: `Module ${modules.length + 1}`,
      code: "",
      riskScan: null
    };
    setModules([...modules, newModule]);
  };
  const handleRemoveModule = (id: string) => {
    if (modules.length === 1) {
      toast.error("You must have at least one module");
      return;
    }

    // Check if module has content
    const module = modules.find(m => m.id === id);
    const hasContent = module && (module.name.trim() || module.code.trim());
    if (hasContent) {
      setModuleToDelete(id);
    } else {
      confirmRemoveModule(id);
    }
  };
  const confirmRemoveModule = (id: string) => {
    setModules(modules.filter(m => m.id !== id));
    setModuleToDelete(null);
    toast.success("Module removed");
  };
  const handleModuleChange = (id: string, field: "name" | "code", value: string) => {
    const updatedModules = modules.map(m => {
      if (m.id === id) {
        const updated = {
          ...m,
          [field]: value
        };
        // Auto-scan when code changes and has content
        if (field === "code" && value.trim()) {
          const result = scanVbaCode(value);
          updated.riskScan = result;
        }
        return updated;
      }
      return m;
    });
    setModules(updatedModules);
  };
  const handleModuleFileUpload = async (id: string, file: File) => {
    if (!file.name.endsWith(".bas")) {
      toast.error("Please upload a .bas file");
      return;
    }
    const text = await file.text();
    const moduleName = file.name.replace(".bas", "");
    const result = scanVbaCode(text);
    setModules(modules.map(m => m.id === id ? {
      ...m,
      name: moduleName,
      code: text,
      riskScan: result
    } : m));
    toast.success(`Loaded ${file.name} - ${result.score} risk detected`);
  };
  const handleScanModule = (id: string) => {
    const module = modules.find(m => m.id === id);
    if (!module || !module.code.trim()) {
      toast.error("Please enter code to scan");
      return;
    }
    const result = scanVbaCode(module.code);
    setModules(modules.map(m => m.id === id ? {
      ...m,
      riskScan: result
    } : m));
    toast.success(`Risk scan complete: ${result.score} risk`);
  };
  const getOverallRiskScore = () => {
    const scannedModules = modules.filter(m => m.riskScan);
    if (scannedModules.length === 0) return null;
    const hasHigh = scannedModules.some(m => m.riskScan?.score === "high");
    const hasMedium = scannedModules.some(m => m.riskScan?.score === "medium");
    if (hasHigh) return "high";
    if (hasMedium) return "medium";
    return "low";
  };
  const getAllRiskFlags = (): RiskFlag[] => {
    return modules.flatMap(m => m.riskScan?.flags || []);
  };
  const MAX_FILE_SIZE_MB = 5;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleExcelFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validExtensions = [".xls", ".xlsx", ".xlsm", ".xlsb", ".csv"];
    
    const validFiles: File[] = [];
    const errors: string[] = [];
    
    files.forEach(file => {
      const ext = "." + file.name.split(".").pop()?.toLowerCase();
      if (!validExtensions.includes(ext)) {
        errors.push(`${file.name}: Invalid file type`);
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        errors.push(`${file.name}: File exceeds ${MAX_FILE_SIZE_MB}MB limit`);
        return;
      }
      validFiles.push(file);
    });
    
    if (errors.length > 0) {
      toast.error(errors.join(", "));
    }
    
    const newFiles: ExcelFile[] = validFiles.map(file => ({
      id: Date.now().toString() + Math.random(),
      name: file.name,
      size: file.size,
      file: file
    }));
    setExcelFiles([...excelFiles, ...newFiles]);
    if (newFiles.length > 0) {
      toast.success(`${newFiles.length} file(s) attached`);
    }
  };
  const handleRemoveExcelFile = (id: string) => {
    setExcelFiles(excelFiles.filter(f => f.id !== id));
    toast.success("File removed");
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate input data
    try {
      macroSchema.parse({
        name,
        shortDesc,
        installationGuide,
        semver,
        modules: modules.map(m => ({
          name: m.name,
          code: m.code
        })),
        category: selectedTags.length > 0 ? selectedTags[0] : undefined
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message);
        return;
      }
    }
    // Check if code has changed from original (for edits)
    const combinedCode = modules.map(m => `'========== Module: ${m.name || "Untitled"} ==========\n${m.code}\n`).join("\n");
    const codeHasChanged = !editingMacroId || combinedCode !== originalCodeText;

    // Only require scanning if code has changed
    const allScanned = modules.every(m => m.riskScan !== null);
    if (codeHasChanged && !allScanned) {
      toast.error("Please scan all modules before publishing");
      return;
    }
    const overallRisk = getOverallRiskScore();
    if (!overallRisk) {
      toast.error("Please scan your code first");
      return;
    }
    setLoading(true);
    try {
      // Create slug from name
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

      // Check if user has profile
      const {
        data: profile
      } = (await (supabase as any).from("profiles").select("id").eq("id", user.id).single()) as any;
      if (!profile) {
        toast.error("Profile not found. Please try signing out and back in.");
        setLoading(false);
        return;
      }

      // Upload screenshot if provided
      let screenshotUrl = screenshotPreview || null;
      if (screenshot) {
        const fileExt = screenshot.name.split(".").pop();
        const fileName = `${user.id}/${slug}-${Date.now()}.${fileExt}`;
        const {
          data: uploadData,
          error: uploadError
        } = await supabase.storage.from("macro-screenshots").upload(fileName, screenshot);
        if (uploadError) throw uploadError;
        const {
          data: {
            publicUrl
          }
        } = supabase.storage.from("macro-screenshots").getPublicUrl(uploadData.path);
        screenshotUrl = publicUrl;
      }
      let macro: any;
      if (editingMacroId) {
        // Update existing macro
        const macroData: any = {
          name,
          short_desc: shortDesc,
          installation_guide: installationGuide,
          license,
          os_support: osSupport,
          excel_versions: excelVersions,
          risk_score: overallRisk,
          risk_flags: getAllRiskFlags(),
          is_approved: false // Reset approval status for edits - requires re-approval
        };

        // Only update screenshot if a new one was uploaded
        if (screenshot) {
          macroData.screenshot_url = screenshotUrl;
        }
        const {
          data: updatedMacro,
          error: macroError
        } = (await (supabase as any).from("macros").update(macroData).eq("id", editingMacroId).select().single()) as any;
        if (macroError) throw macroError;
        macro = updatedMacro;
      } else {
        // Create new macro
        const excelFileMetadata = excelFiles.map(f => ({
          name: f.name,
          size: f.size
        }));
        const macroData: any = {
          owner_id: user.id,
          name,
          slug,
          short_desc: shortDesc,
          installation_guide: installationGuide,
          license,
          os_support: osSupport,
          excel_versions: excelVersions,
          risk_score: overallRisk,
          risk_flags: getAllRiskFlags(),
          screenshot_url: screenshotUrl,
          is_published: true,
          is_approved: false // Requires admin approval
        };
        const {
          data: newMacro,
          error: macroError
        } = (await (supabase as any).from("macros").insert(macroData).select().single()) as any;
        if (macroError) throw macroError;
        macro = newMacro;
      }

      // Combine code from all modules
      const combinedCode = modules.map(m => `'========== Module: ${m.name || "Untitled"} ==========\n${m.code}\n`).join("\n");
      
      // Upload Excel files to storage and get URLs
      let uploadedFiles: { name: string; size: number; type: string; url: string }[] = [];
      
      if (excelFiles.length > 0) {
        for (const excelFile of excelFiles) {
          if (!excelFile.file) {
            // Skip restored files without actual File objects
            console.warn(`Skipping ${excelFile.name}: No file data available`);
            continue;
          }
          
          const fileExt = excelFile.name.split('.').pop()?.toLowerCase() || 'xlsx';
          const fileName = `${user.id}/${macro.slug}-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('macro-workbooks')
            .upload(fileName, excelFile.file, {
              cacheControl: '3600',
              upsert: false
            });
          
          if (uploadError) {
            console.error('File upload error:', uploadError);
            toast.error(`Failed to upload ${excelFile.name}`);
            continue;
          }
          
          const { data: publicUrlData } = supabase.storage
            .from('macro-workbooks')
            .getPublicUrl(fileName);
          
          uploadedFiles.push({
            name: excelFile.name,
            size: excelFile.size,
            type: fileExt,
            url: publicUrlData.publicUrl
          });
        }
      }

      // Update macro with code and version data
      const codeUpdateData: any = {
        code_text: combinedCode,
        semver,
        sample_file_url: uploadedFiles.length > 0 ? JSON.stringify(uploadedFiles) : null
      };
      const {
        error: codeUpdateError
      } = await supabase.from("macros").update(codeUpdateData).eq("id", macro.id);
      if (codeUpdateError) throw codeUpdateError;

      // Create tag associations if any tags selected
      if (editingMacroId) {
        // When editing, first delete existing tag associations
        await supabase.from("macro_tags").delete().eq("macro_id", macro.id);
      }
      if (selectedTags.length > 0) {
        const tagData = selectedTags.map(tagId => ({
          macro_id: macro.id,
          tag_id: tagId
        }));
        const {
          error: tagError
        } = (await (supabase as any).from("macro_tags").insert(tagData)) as any;
        if (tagError) console.error("Failed to add tags:", tagError);
      }
      setHasUnsavedChanges(false); // Reset after successful submission
      toast.success(editingMacroId ? "✓ Changes submitted for review! Updates won't be visible until approved by an admin." : "✓ Submission received! Your macro is pending admin review before it goes live.");

      // Delete private collection entry after successful publish
      if (privateCollectionId) {
        await supabase.from("macro_drafts").delete().eq("id", privateCollectionId);
      }
      navigate("/vault");
    } catch (error: any) {
      toast.error(error.message || "Failed to submit macro");
    } finally {
      setLoading(false);
    }
  };
  const handleNavigateWithCheck = (path: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowExitDialog(true);
    } else {
      navigate(path);
    }
  };
  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation("back");
      setShowExitDialog(true);
    } else {
      navigate(-1);
    }
  };
  const confirmExit = () => {
    setHasUnsavedChanges(false);
    setShowExitDialog(false);
    if (pendingNavigation) {
      if (pendingNavigation === "back") {
        navigate(-1);
      } else {
        navigate(pendingNavigation);
      }
      setPendingNavigation(null);
    }
  };
  const handleAddVbaCode = () => {
    // Scroll to code editor and focus
    const firstModule = document.getElementById("module-1");
    if (firstModule) {
      firstModule.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      setTimeout(() => {
        codeEditorRef?.focus();
      }, 500);
    }
  };
  return <div className="min-h-screen bg-background">
      <Helmet>
        <title>{editingMacroId ? "Edit Macro" : "Upload Macro"} - MacroVault</title>
        <meta name="description" content={editingMacroId ? "Edit your Excel VBA macro on MacroVault" : "Upload and share your Excel VBA macros with the community"} />
        <link rel="canonical" href="https://findmacros.com/upload" />
      </Helmet>
      <Header onBackClick={handleBackClick} onNavigate={handleNavigateWithCheck} />

      {/* Editing Mode Banner */}
      {editingMacroId && <div className="sticky top-20 z-50 bg-yellow-500/90 dark:bg-yellow-600/90 border-b border-yellow-600/50 py-2 px-4">
          <div className="max-w-4xl mx-auto">
            <p className="text-sm text-yellow-900 dark:text-yellow-100 text-center">
              <span className="font-semibold">Editing Mode:</span> You're editing an existing macro. Changes must be
              submitted for review and won't appear until approved by an admin.
            </p>
          </div>
        </div>}

      <ImageCropModal open={cropModalOpen} onClose={() => setCropModalOpen(false)} imageSrc={tempImageSrc} onCropComplete={handleCropComplete} />

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmExit}>Leave Without Saving</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!moduleToDelete} onOpenChange={open => !open && setModuleToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Module?</AlertDialogTitle>
            <AlertDialogDescription>
              This module contains code or a name. Are you sure you want to delete it? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => moduleToDelete && confirmRemoveModule(moduleToDelete)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Top gradient mask for scrolling form content */}
      <div className="fixed top-0 left-0 right-0 h-32 bg-gradient-to-b from-background via-background/80 to-transparent pointer-events-none z-[45] transition-opacity duration-300" style={{
      opacity: scrollY > 20 ? 1 : 0,
      pointerEvents: "none"
    }} />

      <AddVbaCodeModal onAddCode={handleAddVbaCode} />

      <div className="container mx-auto px-4 pb-12 max-w-[1200px] relative z-[40] pt-24">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Two Column Layout */}
          <div className="flex flex-col-reverse lg:grid lg:grid-cols-[1fr,1fr] gap-6">
            {/* Left Column */}
            <div className="space-y-8">
              {/* Section 1: Name + Description */}
              <Card className="rounded-xl border-0 shadow-none bg-transparent">
                <CardContent className="p-0 space-y-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Label htmlFor="name" className="text-sm font-semibold text-foreground">
                        Title (required)
                      </Label>
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                              <span className="text-xs text-muted-foreground">?</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="z-[9999]">
                            <p className="max-w-xs">
                              Give your macro a clear, descriptive title that explains what it does. Example:
                              "Auto-Format Sales Report" or "Data Validation Helper"
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="relative border-2 border-border rounded-xl p-4 focus-within:border-primary transition-colors">
                      <Input id="name" value={name} onChange={e => setName(e.target.value)} placeholder="Add a title that describes your macro" className="border-0 p-0 text-base focus-visible:ring-0 focus-visible:ring-offset-0" maxLength={100} required />
                      <div className="absolute bottom-3 right-4 text-xs text-muted-foreground">{name.length}/100</div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Label htmlFor="shortDesc" className="text-sm font-semibold text-foreground">
                        Description
                      </Label>
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                              <span className="text-xs text-muted-foreground">?</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="z-[9999]">
                            <p className="max-w-xs">
                              Describe what your macro does, what problems it solves, and who would find it useful. Keep
                              it concise and focused.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="relative border-2 border-border rounded-xl p-4 focus-within:border-primary transition-colors">
                      <Textarea id="shortDesc" value={shortDesc} onChange={e => setShortDesc(e.target.value)} placeholder="Tell viewers what your macro does" className="border-0 p-0 text-base min-h-[200px] resize-none focus-visible:ring-0 focus-visible:ring-offset-0" maxLength={1000} required />
                      <div className="absolute bottom-3 right-4 text-xs text-muted-foreground">
                        {shortDesc.length}/1000
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Category */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <Label htmlFor="category" className="text-sm font-semibold text-foreground">
                    Category *
                  </Label>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                          <span className="text-xs text-muted-foreground">?</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="z-[9999]">
                        <p className="max-w-xs">
                          Choose the category that best describes your macro's primary function. This helps users find
                          your macro when browsing.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="relative border-2 border-border rounded-xl p-3 focus-within:border-primary transition-colors">
                  <Select value={selectedTags.length > 0 ? selectedTags[0].toString() : ""} onValueChange={value => {
                  const tagId = parseInt(value);
                  setSelectedTags([tagId]);
                }}>
                    <SelectTrigger className="border-0 p-0 h-9 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {tags.map(tag => <SelectItem key={tag.id} value={tag.id.toString()}>
                          {tag.name}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Preview Image */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <Label className="text-sm font-semibold text-foreground">Thumbnail (optional)</Label>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                          <span className="text-xs text-muted-foreground">?</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="z-[9999]">
                        <p className="max-w-xs">
                          Upload a screenshot or image showing your macro in action. This helps users understand what to
                          expect. Recommended size: 16:9 aspect ratio.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div onDrop={handleImageDrop} onDragOver={handleImageDragOver} onDragLeave={handleImageDragLeave} className={cn("relative border-2 border-dashed rounded-lg p-8 transition-colors", isDraggingImage ? "border-primary bg-primary/5" : "border-border")}>
                  <Input id="screenshot" type="file" accept="image/*" onChange={handleScreenshotChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                  <div className="text-center pointer-events-none">
                    <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Upload preview image or supporting files (optional)</p>
                  </div>
                </div>
                {screenshotPreview && <div className="space-y-3">
                    <div className="relative w-full aspect-video rounded-lg overflow-hidden border">
                      <img src={screenshotPreview} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => {
                          setScreenshotPreview("");
                          setScreenshot(null);
                          setOriginalImageSrc("");
                          setHasUnsavedChanges(true);
                        }}
                        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors"
                      >
                        <X className="h-4 w-4 text-white" />
                      </button>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={handleReCrop} className="w-full">
                      Re-crop Image
                    </Button>
                  </div>}
              </div>

              {/* Installation & Usage Guide (optional, collapsible) */}
              <Collapsible>
                <div className="space-y-2">
                  <CollapsibleTrigger asChild>
                    <button type="button" className="flex items-center gap-2 w-full hover:text-foreground transition-colors">
                      <Label className="text-sm font-semibold text-foreground cursor-pointer">
                        Installation & Usage Guide (optional)
                      </Label>
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center" onClick={e => e.stopPropagation()}>
                              <span className="text-xs text-muted-foreground">?</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="z-[9999]">
                            <p className="max-w-xs">
                              Provide step-by-step instructions on how to install and use your macro. Include any
                              prerequisites, setup steps, and usage examples.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <ChevronDown className="h-4 w-4 transition-transform ui-open:rotate-180" />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2">
                    <ReactQuill theme="snow" value={installationGuide} onChange={setInstallationGuide} placeholder="Add user instructions, FAQ's, or any additional info..." modules={{
                    toolbar: [[{
                      header: [1, 2, 3, false]
                    }], ["bold", "italic", "underline", "strike"], [{
                      list: "ordered"
                    }, {
                      list: "bullet"
                    }], [{
                      indent: "-1"
                    }, {
                      indent: "+1"
                    }], ["link"], ["clean"]],
                    clipboard: {
                      matchVisual: false
                    }
                  }} className="rich-text-editor bg-background [&_.ql-editor]:min-h-[300px]" />
                  </CollapsibleContent>
                </div>
              </Collapsible>
            </div>

            {/* Right Column - Sticky */}
            <div className="space-y-4 lg:sticky lg:top-24 h-fit">
              {/* Action Buttons */}
              <div className="flex gap-3 my-0 py-[29px]">
                <Button type="button" variant="outline" onClick={saveForMyself} disabled={savingPrivate} className={cn("flex-1 rounded-full transition-colors duration-300", privateJustSaved && "bg-green-500/20 hover:bg-green-500/30 border-green-500/50 text-green-700 dark:text-green-300")}>
                  <Save className="h-4 w-4 mr-2" />
                  {savingPrivate ? "Saving..." : privateJustSaved ? "Saved!" : "Save as Draft"}
                </Button>
                <Button type="submit" disabled={loading || !modules.every(m => m.riskScan !== null)} className="flex-1 rounded-full">
                  <Upload className="h-4 w-4 mr-2" />
                  {loading ? "Publishing..." : "Publish Macro"}
                </Button>
              </div>

              {/* Your Macro Code */}
              <Card className="rounded-xl border-0 shadow-none -mt-0 bg-transparent">
                <CardContent className="space-y-4 px-0 pt-0">
                  {modules.map((module, index) => <div key={module.id} id={`module-${module.id}`} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1">
                          <Input value={module.name} onChange={e => handleModuleChange(module.id, "name", e.target.value)} placeholder={`Module ${index + 1}`} className="h-8 bg-transparent border-0 px-0 text-sm font-medium focus-visible:ring-0 focus-visible:ring-offset-0 flex-1" maxLength={50} />
                          {index === 0 && (
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs text-muted-foreground">?</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="z-[9999]">
                                  <p className="max-w-xs">
                                    Name your VBA module (e.g., "Main", "Helpers"). Add your VBA code below to share with the community.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        {modules.length > 1 && <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleRemoveModule(module.id)} className="text-destructive focus:text-destructive">
                                <X className="h-4 w-4 mr-2" />
                                Delete Module
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>}
                      </div>

                      <CodeEditor code={module.code} onChange={value => handleModuleChange(module.id, "code", value || "")} height="200px" />
                    </div>)}

                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={handleAddModule} className="flex-1">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Module
                    </Button>
                    <Input type="file" accept=".bas" onChange={e => {
                    const file = e.target.files?.[0];
                    if (file && modules.length > 0) {
                      handleModuleFileUpload(modules[modules.length - 1].id, file);
                    }
                  }} className="hidden" id="file-upload-global" />
                    <Button type="button" variant="outline" onClick={() => document.getElementById("file-upload-global")?.click()}>
                      <FileUp className="h-3 w-3 mr-2" />
                      Upload .bas
                    </Button>
                  </div>

                  {/* Excel Files */}
                  {excelFiles.length > 0 && <div className="pt-4 border-t space-y-3">
                      <div className="text-sm font-medium">Excel Files:</div>
                      <div className="space-y-2">
                        {excelFiles.map(file => <div key={file.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{file.name}</p>
                              <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(2)} KB</p>
                            </div>
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveExcelFile(file.id)} className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive">
                              <X className="h-4 w-4" />
                            </Button>
                          </div>)}
                      </div>
                    </div>}
                </CardContent>
              </Card>

              {/* Add Excel File */}
              <Card className="rounded-xl border-0 shadow-none bg-transparent">
                <CardContent className="p-0">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Excel Files (optional)</Label>
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                              <span className="text-xs text-muted-foreground">?</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="z-[9999]">
                            <p className="max-w-xs">
                              Upload Excel workbooks that demonstrate your macro or serve as sample files. This helps
                              users understand how to use your macro with real examples.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="relative border-2 border-dashed rounded-lg p-8 transition-colors border-border">
                      <Input type="file" accept=".xls,.xlsx,.xlsm,.xlsb,.csv" multiple onChange={handleExcelFileUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                      <div className="text-center pointer-events-none">
                        <FileUp className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Upload Excel workbooks (optional)</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>

        <SubmissionPreview open={showPreview} onClose={() => setShowPreview(false)} name={name} shortDesc={shortDesc} installationGuide={installationGuide} modules={modules} excelFiles={excelFiles} semver={semver} screenshotPreview={screenshotPreview} tags={tags} selectedTags={selectedTags} />
      </div>
    </div>;
};
export default Submit;